import { IButtonStyles } from "@fluentui/react";
import { IMenuItemStyles } from "@fluentui/react/lib/ContextualMenu";
import {
  getHighContrastNoAdjustStyle,
  HighContrastSelector,
  IRawStyle,
  ITheme,
  mergeStyleSets,
} from "@fluentui/react/lib/Styling";
import { memoizeFunction } from "@fluentui/react/lib/Utilities";

import {
  baseFabricFocusSelector,
  commonHighContrastButtonRestStyles,
  commonHighContrastDisabledStyles,
  commonHighContrastHoveredDisabledStyles,
  commonHighContrastOutlineFocusedDisabled,
  commonHighContrastOutlineHoverDisabled,
  commonHighContrastOutlineHoverFocus,
  controlStylingConstants,
  getMenuButtonStateStyles,
  getStandardControlSemanticColorsWithTheme,
  hiddenContentStyle,
  IMenuItemClassNames,
  StandardControlSemanticColors,
} from "@1js/acui-common";

export type ControlInMenuStylesFunction = (
  disabled?: boolean,
  expanded?: boolean,
  checked?: boolean,
  primaryDisabled?: boolean
) => Partial<IMenuItemStyles>;

export type ControlInMenuStyleFnOrObject =
  | Partial<IMenuItemStyles>
  | ControlInMenuStylesFunction;

export const getStylesFromControlInMenuFnOrObject = memoizeFunction(
  (
    styleFnOrObject: ControlInMenuStyleFnOrObject,
    disabled?: boolean,
    expanded?: boolean,
    checked?: boolean,
    primaryDisabled?: boolean
  ) => {
    if (typeof styleFnOrObject === "function") {
      return styleFnOrObject(disabled, expanded, checked, primaryDisabled);
    }
    return styleFnOrObject;
  }
);

export const menuButtonIconStyles = memoizeFunction(
  (theme: ITheme): IRawStyle => {
    return {
      display: "inline-block",
      verticalAlign: "middle",
      flexShrink: "0",
      width: 20,
      height: 20,
      lineHeight: 20,
      margin: 0,
      padding: "0 10px 0 0",
      fill: getStandardControlSemanticColorsWithTheme(theme).textColor,
    };
  }
);

const HCActiveHoverFocusStyles: IRawStyle = {
  selectors: {
    [HighContrastSelector]: {
      backgroundColor: "Highlight",
      color: "HighlightText",
      outlineColor: "ButtonText",
      ...getHighContrastNoAdjustStyle(),
    },
  },
};

export const getClassNamesForMenuButton = (
  customMenuItemStyles?: ControlInMenuStyleFnOrObject,
  customControlStyles?: IButtonStyles,
  ...rest: (ControlInMenuStyleFnOrObject | undefined)[]
) => {
  return memoizeFunction(
    (
      theme: ITheme,
      disabled: boolean,
      expanded: boolean
    ): IMenuItemClassNames => {
      // Merge custom styles together
      const styles: Partial<IMenuItemStyles>[] = [];
      if (customMenuItemStyles) {
        styles.push(
          getStylesFromControlInMenuFnOrObject(
            customMenuItemStyles,
            disabled,
            expanded
          )
        );
      }
      for (const r of rest) {
        if (r) {
          styles.push(
            getStylesFromControlInMenuFnOrObject(r, disabled, expanded)
          );
        }
      }
      const combinedCustomStyles = mergeStyleSets(...styles);

      const standardControlSemanticColors: StandardControlSemanticColors =
        getStandardControlSemanticColorsWithTheme(theme);
      return mergeStyleSets(
        {
          item: [
            {
              position: "relative",
              boxSizing: "border-box",
              selectors: {
                ":hover": commonHighContrastOutlineHoverFocus,
                ":focus": commonHighContrastOutlineHoverFocus,
              },
            },
            disabled && [
              commonHighContrastDisabledStyles,
              {
                selectors: {
                  ":hover": [
                    commonHighContrastOutlineHoverDisabled,
                    commonHighContrastHoveredDisabledStyles,
                  ],
                  ":focus": [
                    commonHighContrastOutlineFocusedDisabled,
                    commonHighContrastDisabledStyles,
                  ],
                },
              },
            ],
          ],
          root: [
            {
              font: "inherit",
              color: standardControlSemanticColors.textColor,
              width: "100%",
              textAlign: "left",
              backgroundColor: "transparent",
              boxSizing: "border-box",
              border: "1px solid transparent",
              borderRadius: 0,
              display: "block",
              cursor: "default",
              height: controlStylingConstants.menuItemHeight,
              lineHeight: controlStylingConstants.menuItemHeight,
              padding: "0 15px 0 9px",
              selectors: {
                ":hover": HCActiveHoverFocusStyles,
                ":focus": HCActiveHoverFocusStyles,
                ":active": HCActiveHoverFocusStyles,
              },
            },
            commonHighContrastButtonRestStyles,
            disabled && [
              {
                color: standardControlSemanticColors.textDisabledColor,
                pointerEvents: "none",
                userSelect: "none",
                selectors: {
                  ":hover": [
                    commonHighContrastOutlineHoverDisabled,
                    commonHighContrastHoveredDisabledStyles,
                  ],
                  ":focus": [
                    commonHighContrastOutlineFocusedDisabled,
                    commonHighContrastDisabledStyles,
                  ],
                },
              },
              commonHighContrastDisabledStyles,
            ],
            getMenuButtonStateStyles(theme, expanded, disabled),
            {
              selectors: {
                [baseFabricFocusSelector]: {
                  backgroundColor: disabled
                    ? standardControlSemanticColors.defaultBackgroundColor
                    : standardControlSemanticColors.menuFocusedBackgroundColor,
                },
              },
            },
          ],
          linkContent: {
            height: controlStylingConstants.menuItemHeight - 2,
            display: "flex",
            alignItems: "center",
            position: "relative",
          },
          icon: [
            menuButtonIconStyles(theme),
            disabled && {
              opacity: 0.4,
              selectors: {
                [HighContrastSelector]: {
                  opacity: 1,
                },
              },
            },
          ],
          label: [
            {
              lineHeight: "normal",
              verticalAlign: "middle",
              display: "inline-block",
              flexGrow: "1",
              ...getHighContrastNoAdjustStyle(),
            },
            disabled && commonHighContrastDisabledStyles,
          ],
          labelDisabled: commonHighContrastDisabledStyles,
          divider: { margin: "3px 8px" },
          checkmarkIcon: {},
          secondaryText: {},
          subMenuIcon: {},
          splitContainer: {},
          splitPrimary: {},
          splitMenu: {},
          linkContentMenu: {},
          screenReaderText: [hiddenContentStyle, { visibility: "hidden" }],
        },
        combinedCustomStyles,
        customControlStyles
      );
    }
  );
};

export const getClassNamesForMLRMenuButton = memoizeFunction(
  (
    customMenuItemStyles?: Partial<IMenuItemStyles>
  ): Partial<IMenuItemClassNames> => {
    return mergeStyleSets(
      {
        root: {
          height: controlStylingConstants.smallMenuItemHeight,
          lineHeight: controlStylingConstants.smallMenuItemHeight,
        },
        linkContent: {
          height: controlStylingConstants.smallMenuItemHeight - 2, // two pixels account for the border around the root
        },
        icon: {
          height: 16,
          width: 16,
          lineHeight: 16,
        },
        label: {
          fontSize: 12,
        },
        subMenuIcon: {
          height: 12,
        },
        secondaryText: { padding: "0 0 0 20px" },
      },
      customMenuItemStyles
    );
  }
);
